import { wait, ErrorStringFromException } from "../utils/utils";
import { RETRY_SETTINGS, MAX_UNRESPONSIVENESS_WAIT_TIME_MILLISECONDS } from "../AppConstants";
import { ILogServiceEvent } from "./interfaces/ILogServiceEvent";

// Converts from 100-nanoseconds to seconds.
export function secFrom100nsec(nsec100: number) {
    return 0.0000001 * nsec100;
}

// Converts from 100-nanoseconds to milliseconds.
export function msFrom100nsec(nsec100: number) {
    return 0.0001 * nsec100;
}

export function secFromMs(ms: number) {
    return ms/1000;
}

export async function RetryServiceCallUntilSuccessOrTimeout<T>(
    logServiceEvent: ILogServiceEvent,
    throwingServiceCall: () => T,
    shouldRetryOnNull: boolean,
    shouldRetryError?: (e: any) => boolean,
    shouldIgnoreTimeout?: () => boolean,
    timeoutMilliseconds: number = MAX_UNRESPONSIVENESS_WAIT_TIME_MILLISECONDS)
    : Promise<T> {
    let retryCount: number = 0;
    let lastError: any = "Failed even after " + RETRY_SETTINGS.maxRetries + " retry attempts. Giving up!";
    do {
        try {
            if (retryCount > 0) {
                await wait(RETRY_SETTINGS.retryWaitMilliseconds);
            }

            const response: T = await new Promise<T>(async (resolve, reject) => {
                try {
                    if (shouldIgnoreTimeout == null || !shouldIgnoreTimeout()) {
                        wait(timeoutMilliseconds).then(()=>{
                            reject("Service call timed out after " + timeoutMilliseconds + " milliseconds");
                        });
                    }
                    resolve(await throwingServiceCall());
                } catch (error) {
                    lastError = error;
                    reject(error);
                }
            });

            if (response == null && shouldRetryOnNull) {
                logServiceEvent.AddResponse({ isSuccessful: false, message: "null", isFinalResponse: false });
                continue;
            }

            logServiceEvent.AddResponse({ isSuccessful: true, message: "Success", isFinalResponse: true });
            return response;
        } catch (e) {
            lastError = e;
            const isRetryable: boolean = (shouldRetryError == null) || shouldRetryError(e);
            logServiceEvent.AddResponse({ isSuccessful: false, message:  ErrorStringFromException(e), isFinalResponse: !isRetryable});

            // If the caller doesn't want us to retry this error, throw the error back to the caller.
            if (!isRetryable) {
                throw e;
            }
        }
    } while(retryCount++ < RETRY_SETTINGS.maxRetries);

    logServiceEvent.AddResponse({isSuccessful: false, message: ErrorStringFromException(lastError), isFinalResponse: true});
    throw lastError;
}

// Parse a number from the string representation of SpeakerId that the speech service gives us.
// e.g. "Guest_1" => 1
export function GetNumericSpeakerIdFromSpeakerId(speakerId?: string): number | undefined {
    if (speakerId == null)
      return undefined;

    // Get groups of digits from the input string.
    const matches = speakerId.match(/\d+/);

    if (matches == null || matches.length == 0)
      return undefined;

    // Return the first group of digits we find as number.
    return parseInt(matches[0]);
  }